import React, { useState, useEffect, useCallback } from "react";
import TabBar from '../../components/Tabs/TabBar';
import TabBody from "../../components/Tabs/TabBody";
import { postTabContent } from '../../api';
import '../../App.css';

const PasteEditor = () => {
    const [tabs, setTabs] = useState([{ title: "", content: "" }]);
    const [activeTab, setActiveTab] = useState(0);
    const [isClosing, setIsClosing] = useState(false);

    const handleTabClick = useCallback((index) => {
        if (!isClosing) setActiveTab(index);
    }, [isClosing]);

    const addTab = useCallback(() => {
        setTabs(prev => [...prev, { title: "", content: "" }]);
        requestAnimationFrame(() => {
            setActiveTab(prev => prev + (tabs.length - prev));
            const tabElements = document.querySelectorAll('.tab');
            const newTabElement = tabElements[tabElements.length - 1];
            if (newTabElement) {
                newTabElement.classList.add('new-tab');
                setTimeout(() => newTabElement.classList.remove('new-tab'), 250);
            }
        });
    }, [tabs.length]);

    const closeTab = useCallback((index) => {
        setIsClosing(true);

        let newActiveTab;
        if (index === activeTab) {
            newActiveTab = index === tabs.length - 1 ? Math.max(index - 1, 0) : index;
        } else {
            newActiveTab = index < activeTab ? activeTab - 1 : activeTab;
        }

        let newTabs = [...tabs];
        newTabs.splice(index, 1);

        if (newTabs.length === 0) {
            newTabs = [{ title: "", content: "" }];
            newActiveTab = 0;
        }

        requestAnimationFrame(() => {
            setTabs(newTabs);
            setActiveTab(newActiveTab);
            setTimeout(() => setIsClosing(false), 250);
        });
    }, [tabs, activeTab]);

    const postTab = useCallback(async () => {
        const payload = tabs.map(tab => ({
            ...tab,
            content: tab.content + '\n',
        }));

        try {
            const uniqueID = await postTabContent(payload);
            if (uniqueID) window.location.replace(`/${uniqueID}`);
        } catch (error) {
            console.error('Error posting tab details:', error);
        }
    }, [tabs]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (isClosing) return;

            const mod = event.ctrlKey || event.metaKey;
            if (mod && event.key === 'n') {
                event.preventDefault();
                addTab();
            } else if (mod && event.key === 's') {
                event.preventDefault();
                postTab();
            } else if (mod && event.key === 'w') {
                event.preventDefault();
                if (tabs.length > 1) closeTab(activeTab);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [tabs, activeTab, isClosing, addTab, closeTab, postTab]);

    const handleTitleChange = useCallback((event) => {
        const value = event.target.value.slice(0, 60);
        setTabs(prev => prev.map((tab, i) => i === activeTab ? { ...tab, title: value } : tab));
    }, [activeTab]);

    const handleContentChange = useCallback((event) => {
        const value = event.target.value;
        setTabs(prev => prev.map((tab, i) => i === activeTab ? { ...tab, content: value } : tab));
    }, [activeTab]);

    const reorderTabs = useCallback((fromIndex, toIndex) => {
        setTabs(prev => {
            const updated = [...prev];
            const [moved] = updated.splice(fromIndex, 1);
            updated.splice(toIndex, 0, moved);
            return updated;
        });
        // Update activeTab to follow the moved tab
        if (activeTab === fromIndex) {
            setActiveTab(toIndex);
        } else if (fromIndex < activeTab && toIndex >= activeTab) {
            setActiveTab(activeTab - 1);
        } else if (fromIndex > activeTab && toIndex <= activeTab) {
            setActiveTab(activeTab + 1);
        }
    }, [activeTab]);

    return (
        <div className="App">
            <TabBar
                editDisabled={0}
                tabs={tabs}
                activeTab={activeTab}
                onTabClick={handleTabClick}
                onAddTab={addTab}
                onCloseTab={closeTab}
                onSave={postTab}
                onReorderTabs={reorderTabs}
            />
            <TabBody
                editDisabled={0}
                tabs={tabs}
                activeTab={activeTab}
                handleContentChange={handleContentChange}
                handleTitleChange={handleTitleChange}
            />
            <div className="status-bar">
                <div className="status-bar-left">
                    <span className="status-item">
                        <kbd>Ctrl+S</kbd> Save
                    </span>
                    <span className="status-item">
                        <kbd>Ctrl+N</kbd> New tab
                    </span>
                    <span className="status-item">
                        <kbd>Ctrl+W</kbd> Close tab
                    </span>
                </div>
                <div className="status-bar-right">
                    <span className="status-item">
                        {tabs.length} tab{tabs.length !== 1 ? 's' : ''}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default PasteEditor;
